import React, { useState, useRef } from 'react';
import { Search, Book, Loader, AlertTriangle, Filter, X } from 'lucide-react';
import { HelpButton } from '../components/common/HelpButton';
import { searchBible } from '../lib/api';
import { VerseCard } from '../components/VerseCard';
import { BiblicalThemes } from '../components/BiblicalThemes';
import type { BibleVerse } from '../lib/types';
import { useBibleStore } from '../store/bibleStore';

type SearchTab = 'search' | 'themes';

const SORT_OPTIONS = [
  { value: 'relevance', label: 'Relevance' },
  { value: 'bookOrder', label: 'Book Order' }
];

const RESULTS_PER_PAGE_OPTIONS = [10, 20, 50, 100];

// Bible books in order for sorting
const BIBLE_BOOKS_ORDER = [
  'Genesis', 'Exodus', 'Leviticus', 'Numbers', 'Deuteronomy',
  'Joshua', 'Judges', 'Ruth', '1 Samuel', '2 Samuel', '1 Kings', '2 Kings',
  '1 Chronicles', '2 Chronicles', 'Ezra', 'Nehemiah', 'Esther',
  'Job', 'Psalms', 'Proverbs', 'Ecclesiastes', 'Song of Solomon',
  'Isaiah', 'Jeremiah', 'Lamentations', 'Ezekiel', 'Daniel',
  'Hosea', 'Joel', 'Amos', 'Obadiah', 'Jonah', 'Micah',
  'Nahum', 'Habakkuk', 'Zephaniah', 'Haggai', 'Zechariah', 'Malachi',
  'Matthew', 'Mark', 'Luke', 'John', 'Acts',
  'Romans', '1 Corinthians', '2 Corinthians', 'Galatians', 'Ephesians',
  'Philippians', 'Colossians', '1 Thessalonians', '2 Thessalonians',
  '1 Timothy', '2 Timothy', 'Titus', 'Philemon', 'Hebrews',
  'James', '1 Peter', '2 Peter', '1 John', '2 John', '3 John', 'Jude',
  'Revelation'
];

export function SearchPage() {
  const [activeTab, setActiveTab] = useState<SearchTab>('search');
  const [searchResults, setSearchResults] = useState<BibleVerse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastQuery, setLastQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const selectedVersion = useBibleStore((state) => state.panels[0]?.bibleId || 'kjv');

  // Filter states
  const [testament, setTestament] = useState<'all' | 'old' | 'new'>('all');
  const [sortBy, setSortBy] = useState<'relevance' | 'bookOrder'>('relevance');
  const [resultsPerPage, setResultsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  const filterResults = (results: BibleVerse[]) => {
    let filtered = [...results];

    // Apply testament filter
    if (testament !== 'all') {
      const isNewTestament = (book: string) => 
        ['Matthew', 'Mark', 'Luke', 'John', 'Acts', 'Romans', '1 Corinthians', '2 Corinthians', 
         'Galatians', 'Ephesians', 'Philippians', 'Colossians', '1 Thessalonians', '2 Thessalonians',
         '1 Timothy', '2 Timothy', 'Titus', 'Philemon', 'Hebrews', 'James', '1 Peter', '2 Peter',
         '1 John', '2 John', '3 John', 'Jude', 'Revelation'].includes(book);
      
      filtered = filtered.filter(verse => 
        testament === 'new' ? isNewTestament(verse.book) : !isNewTestament(verse.book)
      );
    }

    // Apply sorting
    if (sortBy === 'bookOrder') {
      filtered.sort((a, b) => {
        const bookCompare = BIBLE_BOOKS_ORDER.indexOf(a.book) - BIBLE_BOOKS_ORDER.indexOf(b.book);
        if (bookCompare !== 0) return bookCompare;
        if (a.chapter !== b.chapter) return a.chapter - b.chapter;
        return a.verse - b.verse;
      });
    }

    return filtered;
  };

  const handleSearch = async (query: string) => {
    if (!query.trim() || loading) return;

    setLoading(true);
    setError(null);
    setLastQuery(query);
    setSearchResults([]);
    setCurrentPage(1);

    try {
      const results = await searchBible(selectedVersion, query, resultsPerPage);
      setSearchResults(results);
      if (results.length === 0) {
        setError('No results found. Try a different search term or check your spelling.');
      }
    } catch (err) {
      console.error('Search error:', err);
      setError(err instanceof Error ? err.message : 'Failed to search. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchInputRef.current) {
      handleSearch(searchInputRef.current.value);
    }
  };

  const handleResultsPerPageChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = Number(e.target.value);
    setLoading(true);
    try {
      if (lastQuery) {
        const results = await searchBible(selectedVersion, lastQuery, newValue);
        setSearchResults(results);
      }
    } catch (err) {
      console.error('Search error:', err);
      setError(err instanceof Error ? err.message : 'Failed to search. Please try again.');
    } finally {
      setResultsPerPage(newValue);
      setCurrentPage(1);
      setLoading(false);
    }
  };

  const filteredResults = filterResults(searchResults);
  const paginatedResults = filteredResults.slice(
    (currentPage - 1) * resultsPerPage,
    currentPage * resultsPerPage
  );
  const totalPages = Math.ceil(filteredResults.length / resultsPerPage);

  const tabs = [
    {
      id: 'search',
      label: 'Search',
      icon: Search
    },
    {
      id: 'themes',
      label: 'Biblical Themes',
      icon: Book
    }
  ];

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-semibold">Search</h1>
          <HelpButton pageName="search" className="ml-2" />
        </div>
      </div>
      <div className="min-h-screen p-4 sm:p-6 md:p-8">
        <header className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Search className="w-8 h-8 text-gold-500" />
            <h1 className="text-2xl sm:text-3xl font-bold text-white">Bible Search</h1>
          </div>
          <p className="text-white/60">
            Search through scripture or explore biblical themes.
          </p>
        </header>

        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 mb-6" data-tutorial="search-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as SearchTab)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors
                ${activeTab === tab.id 
                  ? 'bg-gold-500/20 text-gold-300 border border-gold-500/30' 
                  : 'text-white/60 hover:bg-white/5 border border-transparent'
                }`}
            >
              <tab.icon size={18} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="bg-white/5 backdrop-blur-lg rounded-xl border border-white/10 p-4 sm:p-6">
          {activeTab === 'search' ? (
            <>
              <form onSubmit={handleSubmit} className="mb-6">
                <div className="relative" data-tutorial="search-input">
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search by keyword, phrase, or reference (e.g., 'love', 'in the beginning', 'John 3:16')"
                    className="input-primary w-full pl-12 pr-4 py-3"
                    disabled={loading}
                  />
                  <Search 
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" 
                    size={20} 
                  />
                </div>
                <p className="mt-2 text-sm text-white/60">
                  Currently searching in {selectedVersion.toUpperCase()} version
                </p>
              </form>

              {/* Filter Toggle */}
              <div className="flex items-center gap-2 mb-6">
                <p className="text-white/60">
                  Found {searchResults.length} results for "{lastQuery}"
                  {paginatedResults.length !== searchResults.length && (
                    <span className="text-white/40">
                      {' '}(showing {paginatedResults.length} per page)
                    </span>
                  )}
                </p>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gold-500/10 text-gold-400 hover:bg-gold-500/20 transition-colors"
                >
                  {showFilters ? <X size={16} /> : <Filter size={16} />}
                  {showFilters ? 'Hide Filters' : 'Show Filters'}
                </button>
              </div>

              {/* Collapsible Filter Section */}
              {showFilters && (
                <div className="p-4 bg-white/5 rounded-lg mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm text-white/60">Testament</label>
                      <select
                        className="select-primary w-full bg-gray-800 text-white border border-gold-500/20 rounded-lg px-3 py-2 focus:outline-none focus:border-gold-500/50"
                        value={testament}
                        onChange={(e) => setTestament(e.target.value as 'all' | 'old' | 'new')}
                      >
                        <option value="all">All Testaments</option>
                        <option value="old">Old Testament</option>
                        <option value="new">New Testament</option>
                      </select>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm text-white/60">Sort By</label>
                      <select
                        className="select-primary w-full bg-gray-800 text-white border border-gold-500/20 rounded-lg px-3 py-2 focus:outline-none focus:border-gold-500/50"
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value as 'relevance' | 'bookOrder')}
                      >
                        {SORT_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm text-white/60">Results per Page</label>
                      <select
                        className="select-primary w-full bg-gray-800 text-white border border-gold-500/20 rounded-lg px-3 py-2 focus:outline-none focus:border-gold-500/50"
                        value={resultsPerPage}
                        onChange={handleResultsPerPageChange}
                      >
                        {RESULTS_PER_PAGE_OPTIONS.map((num) => (
                          <option key={num} value={num}>{num}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* Search Results */}
              <div className="divide-y divide-gold-500/10" data-tutorial="search-results">
                {paginatedResults.map((verse) => (
                  <div key={verse.id} className="py-4 first:pt-0 last:pb-0">
                    <VerseCard
                      verse={verse}
                      showContext={true}
                    />
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-6">
                  <button
                    className="px-3 py-1 rounded bg-gold-500/10 text-gold-400 hover:bg-gold-500/20 disabled:opacity-50"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  <span className="text-white/60">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    className="px-3 py-1 rounded bg-gold-500/10 text-gold-400 hover:bg-gold-500/20 disabled:opacity-50"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          ) : (
            <BiblicalThemes />
          )}
        </div>
      </div>
    </div>
  );
}